/**
 * ActivityView.jsx — the site activity feed (the Activity log area: admins by
 * default, grantable to anyone in the Team page).
 *
 * Reads GET /api/audit (written by server/middleware/audit.js: one row per
 * successful mutation) and renders it as plain-English sentences grouped by
 * day: "Paul Cooke uploaded Invoice 12345 (slate.pdf) — Q5296 · Slate Auto".
 * Raw method+path stay available on hover for debugging, but the feed itself
 * is written for a human skimming "what happened today".
 *
 * The whole trail is reachable: pick a date range (or All time) and page back
 * with "Load older". The search runs on the server, over every row, not just
 * the ones on screen: a quote # (5296 / Q5296), a PO #, a person, or any text.
 * It used to filter only the newest 500 rows, which is about two days.
 *
 * Every line opens the record it touched when clicked, and ends in a Revert
 * button (Sharif, 10/8/26): one line is views/activity/ActivityEntry.jsx, and
 * what each kind of entry opens and how it is undone is
 * views/activity/activityActions.js.
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Icon } from '../components/ui/Icon';
import { useApp } from '../context/AppContext';
import { latestReads, useLiveRefresh } from '../live/useLive';
import { roleLabel, canSeeTab } from '../data/roles';
import { useMyAccess } from '../hooks/useMyAccess';
import ActivityEntry from './activity/ActivityEntry';
import { fieldLabel, fmtVal, identText } from './activity/activityActions';
import { localDay as ymdOf } from '../utils/localDay';
import { amount2 } from '../utils/format';

const GREEN = '#1B5E20';

/* Verb pill per action flavor: create = green, edit = blue, delete = red. */
const FLAVOR = {
  POST: { label: 'ADD', color: '#1B5E20', bg: '#E8F5E9' },
  PUT: { label: 'EDIT', color: '#1565C0', bg: '#E3F2FD' },
  PATCH: { label: 'EDIT', color: '#1565C0', bg: '#E3F2FD' },
  DELETE: { label: 'DELETE', color: '#B71C1C', bg: '#FFEBEE' },
};

const DOC_WORD = { invoice: 'Invoice', po: 'PO', quote: 'Quote', rfq: 'RFQ', general: 'file' };

/* Labels and values (fieldLabel, fmtVal, identText) live in
   views/activity/activityActions.js, which the revert's confirm step shares. */

/* One line per changed field: "PO Value: 54,502 -> 60,000". */
function changeLines(changes) {
  return Object.entries(changes || {}).map(([col, { from, to }]) => ({
    col,
    text: `${fieldLabel(col)}: ${fmtVal(from, col)} → ${fmtVal(to, col)}`,
  }));
}

/**
 * Role and access changes from the Team page (10/5/26). The access routes
 * leave a note on the row (server/routes/users.js: access_user, access_area,
 * access_level, access_was / access_reset / manage_access); a role change is
 * a diff of the users row (changes.role) with the person in its snapshot.
 */
function describeUser(seg, d) {
  const snap = d.snapshot || {};
  let email = snap.email || '';
  try {
    email = email || decodeURIComponent(seg[2] || '');
  } catch {
    email = email || seg[2] || '';
  }
  const who = d.access_user || (snap.name ? `${snap.name} (${email})` : email || 'a user');
  if (seg[3] === 'access') {
    if (d.manage_access) return d.manage_access === 'on' ? `let ${who} manage access` : `stopped ${who} managing access`;
    if (d.access_reset) {
      const n = Number(d.access_count);
      return `reset ${who}'s access to the ${roleLabel(d.access_reset)} role${n ? ` (${n} override${n === 1 ? '' : 's'} removed)` : ''}`;
    }
    if (d.access_area) return `set ${who}'s ${d.access_area} access to ${d.access_level}${d.access_was ? ` (was ${d.access_was})` : ''}`;
    return `changed ${who}'s access`;
  }
  const role = d.changes && d.changes.role;
  if (role) return `changed ${who}'s role from ${roleLabel(role.from)} to ${roleLabel(role.to)}`;
  if (d.role) return `changed ${who}'s role to ${roleLabel(d.role)}`;
  return 'updated a user account';
}

/**
 * Spare Parts (10/8/26): the part, robot or F-number the entry is about comes
 * from the route's own note (routes/spareParts.js req._auditNote) or the
 * created / snapshot row (part_number, f_number). Prices read $1,234.50.
 */
function describeSpareParts(method, seg, d) {
  const made = d.created || {};
  const snap = d.snapshot || {};
  const pn = d.part_number || made.part_number || snap.part_number || 'a part';
  const fn = d.f_number || made.f_number || snap.f_number || '';
  const robot = fn ? `robot ${fn}'s` : "a robot's";
  const money = (v) =>
    v === undefined || v === null || v === '' || v === 'none' || !Number.isFinite(Number(v)) ? 'none' : `$${amount2(Number(v))}`;
  const [, , what, , sub] = seg; // api, spare-parts, parts|robots|inventory|import, id, sub
  if (what === 'import') {
    const lines = (Number(d.lines_created) || 0) + (Number(d.lines_updated) || 0);
    return `imported spare parts (${Number(d.parts_created) || 0} new, ${Number(d.parts_updated) || 0} updated${lines ? `, ${lines} robot list line${lines === 1 ? '' : 's'}` : ''})${d.source ? ` - ${d.source}` : ''}`;
  }
  if (what === 'parts') {
    if (sub === 'price-check') {
      return `checked the price of spare part ${pn}: STANDARD ${money(d.standard_price)}, GLOBAL ${money(d.global_price)}${d.checked_at ? ` (as of ${d.checked_at})` : ''}`;
    }
    if (method === 'POST') return `added spare part ${pn}${made.description ? ` "${made.description}"` : ''}`;
    if (method === 'DELETE') return `deleted spare part ${pn}`;
    return `edited spare part ${pn}`;
  }
  if (what === 'robots') {
    if (sub === 'parts') {
      if (method === 'POST') return `put ${d.qty ? `${d.qty} x ` : ''}${pn} on ${robot} spare parts list`;
      if (method === 'DELETE') return `took ${pn} off ${robot} spare parts list`;
      return `changed ${pn} on ${robot} spare parts list`;
    }
    if (method === 'POST') return `added robot ${fn || ''}`.trim();
    if (method === 'DELETE') return `deleted robot ${fn} and its spare parts list`.replace('  ', ' ');
    return `edited robot ${fn}`.trim();
  }
  if (what === 'inventory') {
    if (sub === 'moves') {
      const why = d.reason ? ` - ${d.reason}` : '';
      return d.direction === 'out'
        ? `took ${d.qty} x ${pn} out of stock (${d.qty_after} left)${why}`
        : `put ${d.qty} x ${pn} into stock (${d.qty_after} on hand)${why}`;
    }
    if (method === 'POST') return `put ${Number(d.qty) || 0} x ${pn} on the shelf${d.location ? ` (${d.location})` : ''}`;
    if (method === 'DELETE') return `removed an empty stock row for ${pn}`;
    return `edited the stock row for ${pn}`;
  }
  return 'changed spare parts';
}

/**
 * Turn one audit row into a readable sentence. Ordered: first match wins.
 * `projName(id)` resolves a project id to "Q1234 · Customer" when known;
 * the row's own snapshot wins over the lookup (it survives later deletes).
 */
function describe(row, projName) {
  const { method, path, detail: d = {} } = row;
  const seg = path.split('/').filter(Boolean); // ['api','projects','bt-0001','attachments']
  const on = (re) => re.test(path);
  const pid = seg[1] === 'projects' ? seg[2] : null;
  const snapName = identText(d.snapshot);
  const proj = snapName ? ` - ${snapName}` : pid ? ` - ${projName(pid)}` : '';

  if (seg[1] === 'spare-parts') return describeSpareParts(method, seg, d);
  if (seg[1] === 'ksa') {
    const verb = method === 'POST' ? 'added' : method === 'DELETE' ? 'deleted' : 'edited';
    const noun =
      seg[2] === 'crm-actions'
        ? 'task'
        : seg[4] === 'comments'
          ? 'comment'
          : seg[4] === 'purchases'
            ? 'purchase'
            : seg[2] === 'fx-rates'
              ? 'exchange rate'
              : seg[3] === 'folders' || seg[4] === 'folders'
                ? 'Doc Hub folder'
                : seg[2] === 'attachments' || seg[4] === 'attachments'
                  ? 'Doc Hub file'
                  : 'record';
    const rate = seg[2] === 'fx-rates' && (d.currency || d.month) ? [d.currency, d.month].filter(Boolean).join(' ') : '';
    const what = snapName || identText(d.created) || d.description || rate || d.customer || d.name || '';
    return `${verb} a GLOBAL KSA ${noun}${what ? ` - ${what}` : ''}`;
  }

  // Doc Hub folders: created under a project, deleted by their own id
  if (on(/\/folders$/) && method === 'POST') return `created a Doc Hub folder${d.name ? ` "${d.name}"` : ''}${proj}`;
  if (on(/\/folders\/[^/]+$/) && method === 'DELETE') return 'deleted a Doc Hub folder';
  // Tasks, named with the project they sit on when it is one we know
  if (seg[1] === 'crm-actions') {
    const t = d.created || d.snapshot || {};
    const home = t.entity_id && projName(t.entity_id) !== t.entity_id ? ` - ${projName(t.entity_id)}` : '';
    const what = t.description ? ` "${t.description}"` : '';
    const verb = method === 'POST' ? 'added a task' : method === 'DELETE' ? 'deleted a task' : 'updated a task';
    return `${verb}${what}${home}`;
  }
  if (on(/\/attachments$/) && method === 'POST') {
    const kind = DOC_WORD[d.doc_type] || 'file';
    const what = d.doc_type && d.doc_type !== 'general' ? `${kind} ${d.doc_ref || ''}`.trim() : 'a file';
    // (an RFQ often has no number, so `kind` alone is the whole phrase)
    return `uploaded ${what}${d.filename ? ` (${d.filename})` : ''}${proj}`;
  }
  if (seg[1] === 'attachments' && method === 'DELETE')
    return `deleted an attachment${d.snapshot?.filename ? ` (${d.snapshot.filename})` : ''}`;
  if (seg[1] === 'attachments' && method === 'PUT') {
    const kind = DOC_WORD[d.doc_type] || 'General';
    return `re-labeled ${d.snapshot?.filename ? `"${d.snapshot.filename}"` : 'an attachment'} to ${kind}${d.doc_ref ? ` ${d.doc_ref}` : ''}`;
  }
  if (on(/\/comments\/[^/]+$/) && method === 'DELETE') return `deleted a note${proj}`;
  if (on(/\/comments$/)) return `added a note${d.body ? ` - "${d.body}"` : ''}${proj}`;
  // These two routes no longer exist, but the audit rows they wrote do. Keep
  // them readable rather than letting old entries render as raw paths.
  if (on(/\/assign-engineer$/)) return `assigned an engineer${proj}`;
  if (on(/\/unassign-engineer$/)) return `removed an engineer${proj}`;
  if (seg[1] === 'projects' && method === 'POST' && !pid)
    return `created project ${identText(d.created) || `"${d.name || d.description || d.customer || 'untitled'}"`}`;
  if (seg[1] === 'projects' && method === 'PUT') return `edited project${proj}`;
  if (seg[1] === 'projects' && method === 'DELETE') return `deleted project${proj || ` ${pid || ''}`}`;
  if (seg[1] === 'accounts') {
    if (method === 'POST' && seg.length === 3 && seg[2] === 'activities') return 'logged a customer activity';
    if (method === 'POST') return `created customer "${d.name || d.company || ''}"`.trim();
    if (method === 'DELETE') return 'deleted a customer record';
    return 'edited a customer record';
  }
  // an approver's decision on a release note (server/lib/releases.js logDecision, 10/12/26)
  if (seg[1] === 'notifications' && seg[2] === 'releases') {
    const t = d.release ? `the release '${d.release}'` : 'a release';
    if (d.release_status === 'notified') return `approved ${t} to notify everyone`;
    if (d.release_status === 'quiet') return `published ${t} quietly, without notifying anyone`;
    if (d.release_status === 'hidden') return `hid ${t} from What's new`;
    return `changed ${t} in What's new`;
  }
  if (seg[1] === 'users') return describeUser(seg, d);
  if (seg[1] === 'chat') return `asked the AI assistant${d.message ? `: "${d.message}"` : ''}`;
  if (seg[1] === 'svm') {
    if (on(/\/check\b/)) return `updated an SVM checklist item${d.state ? ` (${d.state})` : ''}${d.note ? ` - "${d.note}"` : ''}`;
    if (on(/product-data/)) {
      if (method === 'DELETE') return 'removed a product-data entry';
      if (method === 'PUT') return `updated product-data fields${d.equipment ? ` on ${d.equipment}` : ''}`;
      return `added equipment to product data${d.equipment ? ` - ${d.equipment}` : ''}`;
    }
    if (on(/share/)) return 'created a customer share link';
    return 'updated an SVM checklist';
  }
  if (seg[1] === 'engineer' && on(/daily-report/)) return 'submitted a daily report';
  if (seg[1] === 'engineers')
    return method === 'POST' ? 'added an engineer' : method === 'DELETE' ? 'removed an engineer' : 'updated an engineer';
  if (seg[1] === 'staffing') {
    const noun = seg[2] ? seg[2].replace(/s$/, '') : 'record';
    return `${method === 'POST' ? 'added' : method === 'DELETE' ? 'deleted' : 'updated'} a staffing ${noun}`;
  }
  if (seg[1] === 'refs') {
    // the dropdown lists on the opportunity / project forms; DELETE is the red x (10/8/26)
    const list = { customer: 'Customer', engineer: 'Engineer', approval_manager: 'Approval Manager', project_manager: 'Project Manager' }[
      d.kind
    ];
    const where = list ? `the ${list} dropdown` : 'a dropdown list';
    return method === 'DELETE' ? `removed "${d.name || ''}" from ${where}` : `added "${d.name || ''}" to ${where}`;
  }
  // the calendar's tables (10/16/26): the route writes a readable note
  if (seg[1] === 'manpower' && d.calendar_change) return [d.calendar_change, d.calendar_change_more].filter(Boolean).join('; ');
  if (seg[1] === 'manpower') return 'updated the manpower schedule';
  if (seg[1] === 'timesheets') return 'updated timesheets';
  // Fallback: still readable, never blank.
  const verb = method === 'POST' ? 'added to' : method === 'DELETE' ? 'deleted from' : 'updated';
  return `${verb} ${seg[1] || 'the system'}`;
}

function dayLabel(iso) {
  const d = new Date(iso);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const that = new Date(d);
  that.setHours(0, 0, 0, 0);
  const diff = Math.round((today - that) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

/* Date range helpers. Dates are the user's local days; the server gets the
   local-midnight instants, `to` exclusive, so "Sep 30" means all of Sep 30. */
const PAGE = 300;
const daysAgo = (k) => {
  const d = new Date();
  d.setDate(d.getDate() - k);
  return ymdOf(d);
};
// A date input reports every keystroke, so "0202-10-02" passes through while
// a year is typed: only a real date in this century counts.
const okDay = (s) => /^\d{4}-\d{2}-\d{2}$/.test(s) && s >= '2000-01-01' && s <= '2099-12-31';
const startOf = (s) => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d).toISOString();
};
const endOf = (s) => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d + 1).toISOString();
};
const PRESETS = [
  { label: 'Today', from: () => daysAgo(0) },
  { label: 'Last 7 days', from: () => daysAgo(6) },
  { label: 'Last 30 days', from: () => daysAgo(29) },
  { label: 'All time', from: () => '' },
];

const timeOf = (iso) => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
const initialsOf = (name) =>
  String(name || '?')
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

// exported for the tests (src/views/__tests__/teamAccess.test.jsx)
export { describe as describeActivity };

/** id -> row for a list the app has loaded; null while it is empty (not known yet). */
const byId = (list) => (Array.isArray(list) && list.length ? new Map(list.map((x) => [String(x.id), x])) : null);

export default function ActivityView() {
  const { allProjects, allAccounts, allEngineers } = useApp();
  const access = useMyAccess();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState(''); // what is typed
  const [q, setQ] = useState(''); // what is searched (typing settles first)
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const seq = useRef(0); // only the newest request may land
  const pages = useRef(1); // how many pages are on screen
  // A list or an older page is out. A ref, not the loading state: a live
  // refresh can run from the render before that state is set, and must still
  // see the read and stand back.
  const reading = useRef(false);

  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  const datesOk = (!from || okDay(from)) && (!to || okDay(to));
  const rangeOk = datesOk && (!from || !to || from <= to);

  const url = useCallback(
    (before) => {
      const u = new URLSearchParams({ limit: String(PAGE) });
      if (q) u.set('q', q);
      if (from) u.set('from', startOf(from));
      if (to) u.set('to', endOf(to));
      if (before) u.set('before', String(before));
      return `/api/audit?${u}`;
    },
    [q, from, to]
  );

  const load = useCallback(async () => {
    if (!rangeOk) return;
    const mine = ++seq.current;
    pages.current = 1;
    reading.current = true;
    setLoading(true);
    setLoadingMore(false); // a new list never inherits a pending page
    setError('');
    try {
      const r = await fetch(url());
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const page = await r.json();
      if (mine !== seq.current) return;
      setRows(page);
      setHasMore(page.length === PAGE);
    } catch {
      if (mine === seq.current) {
        setRows([]);
        setHasMore(false);
        setError('Could not load the activity log - try Refresh.');
      }
    } finally {
      if (mine === seq.current) {
        reading.current = false;
        setLoading(false);
      }
    }
  }, [url, rangeOk]);
  useEffect(() => {
    load();
  }, [load]);

  // Someone else's change (src/live): re-read the newest page in place, with
  // no "Loading" flash - but only while the screen IS the newest page (no
  // search, no dates, nothing older loaded), so a person reading back through
  // the log is never pulled to the top, and never while a read is out. Asked
  // again when the answer lands: "Load older" may have been pressed while it
  // was out, and the newest page alone must not replace the longer list.
  // It never takes a number of its own (seq): a refresh must never be what
  // makes a list or an older page fail to land - that left "Loading" up for
  // good. Two refreshes land newest-first (latestReads).
  const [fresh] = useState(latestReads);
  const refreshNewest = useCallback(async () => {
    if (q || from || to || pages.current > 1 || reading.current) return;
    const at = seq.current;
    const t = fresh.start();
    try {
      const r = await fetch(url());
      if (!r.ok) return;
      const page = await r.json();
      // a list or an older page read since (seq moved on) is newer than this
      if (at !== seq.current || pages.current !== 1 || !Array.isArray(page) || !fresh.show(t)) return;
      setRows(page);
      setHasMore(page.length === PAGE);
    } catch {
      /* keep what is shown */
    }
  }, [q, from, to, url, fresh]);
  useLiveRefresh(null, refreshNewest);

  // After a revert from this screen: the newest page read again in place (no
  // "Loading" flash, so the line keeps saying it was reverted), the revert's
  // own entry on top. This tab's own save sends it no live event.
  const afterRevert = useCallback(async () => {
    if (reading.current) return;
    const at = seq.current;
    try {
      const r = await fetch(url());
      if (!r.ok) return;
      const page = await r.json();
      if (at !== seq.current || !Array.isArray(page)) return;
      if (pages.current === 1) {
        setRows(page);
        setHasMore(page.length === PAGE);
        return;
      }
      setRows((cur) => [...page.filter((x) => !cur.length || x.id > cur[0].id), ...cur]);
    } catch {
      /* keep what is shown */
    }
  }, [url]);

  // What a click on a line is checked against: the records this app holds
  // (a deleted one says so instead of opening nothing) and the tabs this
  // person may open.
  const jumpCtx = useMemo(() => {
    const contacts = new Map();
    const entries = new Map();
    for (const a of allAccounts || []) {
      for (const c of a.contacts || []) contacts.set(String(c.id), c);
      for (const e of a.recent_activity || []) entries.set(String(e.id), String(e.account_id || e.site_id || a.id));
    }
    const known = Array.isArray(allAccounts) && allAccounts.length > 0;
    return {
      projects: byId(allProjects),
      accounts: byId(allAccounts),
      contacts: known ? contacts : null,
      entries: known ? entries : null,
      engineers: byId(allEngineers),
      canSee: (tab) => canSeeTab(access, tab),
    };
  }, [allProjects, allAccounts, allEngineers, access]);

  // Next page: everything older than the last row on screen. It takes a new
  // number, so a live refresh still out (refreshNewest) can no longer land;
  // the page counts once it is on screen (a failed one leaves live refresh on).
  const loadOlder = async () => {
    const last = rows[rows.length - 1];
    if (!last || !rangeOk) return;
    const mine = ++seq.current;
    reading.current = true;
    setLoadingMore(true);
    setError('');
    try {
      const r = await fetch(url(last.id));
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const page = await r.json();
      if (mine !== seq.current) return; // the search or range changed meanwhile
      pages.current += 1;
      setRows((cur) => [...cur, ...page]);
      setHasMore(page.length === PAGE);
    } catch {
      if (mine === seq.current) setError('Could not load older activity - try again.');
    } finally {
      if (mine === seq.current) {
        reading.current = false;
        setLoadingMore(false);
      }
    }
  };

  const preset = (p) => {
    setFrom(p.from());
    setTo(p.from() ? daysAgo(0) : '');
  };
  const presetOn = (p) => from === p.from() && to === (p.from() ? daysAgo(0) : '');

  // Project id → "Q1234 · Customer" (falls back to the raw id).
  const projName = useCallback(
    (id) => {
      const p = (allProjects || []).find((x) => x.id === id);
      if (!p) return id;
      const q = p.quoteLetter || p.quote_letter;
      return [q ? `Q${q}` : null, p.customer].filter(Boolean).join(' · ') || id;
    },
    [allProjects]
  );

  const groups = useMemo(() => {
    const out = [];
    for (const r of rows) {
      const label = dayLabel(r.at);
      if (!out.length || out[out.length - 1].label !== label) out.push({ label, items: [] });
      out[out.length - 1].items.push(r);
    }
    return out;
  }, [rows]);

  const filtered = !!(q || from || to);
  const chip = (on) => ({
    padding: '6px 12px',
    borderRadius: 16,
    fontSize: 10.5,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'Montserrat',
    border: `1px solid ${on ? GREEN : '#DDE3D8'}`,
    background: on ? GREEN : '#fff',
    color: on ? '#fff' : '#5A6557',
  });
  const dateInput = {
    padding: '7px 10px',
    borderRadius: 8,
    border: '1px solid #DDE3D8',
    fontSize: 11.5,
    fontFamily: 'Montserrat',
    background: '#fff',
  };

  return (
    <div style={{ fontFamily: "'Montserrat', sans-serif" }}>
      {/* Header card */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 800, color: GREEN, letterSpacing: 1.8, textTransform: 'uppercase', marginBottom: 4 }}>
            Admin · Oversight
          </div>
          <div style={{ fontSize: 12.5, color: '#666' }}>
            Every change made on the platform - who did what, and when. Click a line to open what it changed; Revert undoes it (it asks
            first). Reads are not logged.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') setQ(search.trim());
            }}
            placeholder="Search quote # (Q5296), PO #, person or text…"
            aria-label="Search the activity log"
            style={{
              padding: '9px 14px',
              borderRadius: 8,
              border: '1px solid #DDE3D8',
              fontSize: 11.5,
              fontFamily: 'Montserrat',
              outline: 'none',
              minWidth: 300,
              background: '#fff',
            }}
          />
          <button
            type="button"
            onClick={load}
            style={{
              background: GREEN,
              color: '#fff',
              border: 'none',
              padding: '9px 18px',
              borderRadius: 8,
              fontWeight: 700,
              fontSize: 11.5,
              cursor: 'pointer',
              fontFamily: 'Montserrat',
            }}
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Date range: as far back as the log goes. */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
        {PRESETS.map((p) => (
          <button key={p.label} type="button" aria-pressed={presetOn(p)} onClick={() => preset(p)} style={chip(presetOn(p))}>
            {p.label}
          </button>
        ))}
        <span style={{ fontSize: 9.5, fontWeight: 800, color: '#888', letterSpacing: 1.2, textTransform: 'uppercase', marginLeft: 8 }}>
          From
        </span>
        <input
          type="date"
          min="2000-01-01"
          max="2099-12-31"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          aria-label="From date"
          style={dateInput}
        />
        <span style={{ fontSize: 9.5, fontWeight: 800, color: '#888', letterSpacing: 1.2, textTransform: 'uppercase' }}>To</span>
        <input
          type="date"
          min="2000-01-01"
          max="2099-12-31"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          aria-label="To date"
          style={dateInput}
        />
        <span role="status" style={{ fontSize: 10.5, color: rangeOk ? '#999' : '#B71C1C', marginLeft: 6 }}>
          {!datesOk
            ? 'Finish typing the date.'
            : !rangeOk
              ? 'Pick a From date on or before the To date.'
              : loading || error
                ? ''
                : `${rows.length}${hasMore ? '+' : ''} action${rows.length === 1 ? '' : 's'}${filtered ? ' found' : ''} · newest first`}
        </span>
      </div>

      {error && (
        <div
          role="alert"
          style={{
            marginBottom: 12,
            padding: '10px 14px',
            borderRadius: 8,
            background: '#FFEBEE',
            color: '#B71C1C',
            fontSize: 11.5,
            fontWeight: 600,
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ padding: 60, textAlign: 'center', color: '#aaa', fontSize: 12 }}>Loading activity…</div>
      ) : error && rows.length === 0 ? null : groups.length === 0 ? (
        <div
          style={{
            padding: 60,
            textAlign: 'center',
            color: '#bbb',
            fontSize: 12,
            background: '#fff',
            borderRadius: 12,
            border: '1px solid #ECECEC',
          }}
        >
          {filtered
            ? 'Nothing matches that search and date range.'
            : 'No activity recorded yet - actions will appear here as people use the platform.'}
        </div>
      ) : (
        groups.map((g) => (
          <div key={g.label} style={{ marginBottom: 22 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <Icon name="clock" size={13} color={GREEN} />
              <span style={{ fontSize: 11, fontWeight: 800, color: '#0D0D0D', letterSpacing: 1.2, textTransform: 'uppercase' }}>
                {g.label}
              </span>
              <span style={{ fontSize: 10, color: '#aaa', fontWeight: 600 }}>
                {g.items.length} action{g.items.length === 1 ? '' : 's'}
              </span>
              <div style={{ flex: 1, height: 1, background: '#E5E9DD' }} />
            </div>
            <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #ECECEC', overflow: 'hidden' }}>
              {g.items.map((r, i) => {
                const d = r.detail || {};
                const text = describe(r, projName);
                return (
                  <ActivityEntry
                    key={r.id}
                    row={r}
                    time={timeOf(r.at)}
                    initials={initialsOf(r.actor)}
                    flavor={FLAVOR[r.method] || FLAVOR.PUT}
                    text={text}
                    lines={changeLines(d.changes)}
                    created={r.method === 'POST' && d.created ? identText(d.created) : ''}
                    ctx={jumpCtx}
                    access={access}
                    onReverted={afterRevert}
                    last={i === g.items.length - 1}
                  />
                );
              })}
            </div>
          </div>
        ))
      )}

      {/* Paging back: the next 300, older than the last one shown. */}
      {!loading && rangeOk && rows.length > 0 && (
        <div style={{ textAlign: 'center', padding: '6px 0 24px' }}>
          {hasMore ? (
            <button
              type="button"
              onClick={loadOlder}
              disabled={loadingMore}
              style={{
                background: '#fff',
                color: GREEN,
                border: `1px solid ${GREEN}`,
                padding: '9px 22px',
                borderRadius: 8,
                fontWeight: 700,
                fontSize: 11.5,
                cursor: loadingMore ? 'wait' : 'pointer',
                fontFamily: 'Montserrat',
              }}
            >
              {loadingMore ? 'Loading…' : 'Load older'}
            </button>
          ) : (
            <span style={{ fontSize: 10.5, color: '#aaa' }}>
              {filtered ? 'That is everything for this search and date range.' : 'That is everything - the log starts here.'}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
